import React,{useState,useEffect}from'react';
import Layout from'../components/layout/Layout';
import SlidePanel from'../components/common/SlidePanel';
import api from'../services/api';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import{fmtDate}from'../utils/helpers';

const DAYS=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_COLORS=['bg-red-50 border-red-100','bg-blue-50 border-blue-100','bg-indigo-50 border-indigo-100',
  'bg-violet-50 border-violet-100','bg-emerald-50 border-emerald-100','bg-amber-50 border-amber-100','bg-orange-50 border-orange-100'];

const INP='w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400';
const LBL='block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';

export default function DoctorSchedulePage(){
  const {t}=useTranslation();
  const[loading,setLoading]=useState(true);
  const[doctors,setDoctors]=useState<any[]>([]);
  const[selectedDoctor,setSelectedDoctor]=useState('');
  const[schedules,setSchedules]=useState<any[]>([]);
  const[leaves,setLeaves]=useState<any[]>([]);
  const[showForm,setShowForm]=useState(false);
  const[showLeaveForm,setShowLeaveForm]=useState(false);
  const[saving,setSaving]=useState(false);
  const[msg,setMsg]=useState('');
  const[form,setForm]=useState({dayOfWeek:'1',startTime:'09:00',endTime:'17:00',slotDurationMinutes:'15',maxPatients:'',consultationFee:'500'});
  const[leaveForm,setLeaveForm]=useState({leaveDate:'',isFullDay:true,startTime:'',endTime:'',reason:''});

  useEffect(()=>{
    api.getDoctors().then(r=>{
      const list=r?.data?.doctors||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
      setDoctors(list);
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const load=async(doctorId:string)=>{
    if(!doctorId)return;
    try{
      setLoading(true);
      const[s,l]=await Promise.all([api.getDoctorSchedules(doctorId),api.getDoctorLeaves({doctorId})]);
      setSchedules(Array.isArray(s.data)?s.data:s.data?.schedules||[]);
      setLeaves(Array.isArray(l.data)?l.data:l.data?.leaves||[]);
    }catch{}finally{setLoading(false);}
  };

  useEffect(()=>{load(selectedDoctor);},[selectedDoctor]);

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),3500);};

  const saveSchedule=async(e:React.FormEvent)=>{
    e.preventDefault();
    if(!selectedDoctor){notify('⚠️ Select a doctor first');return;}
    setSaving(true);
    try{
      await api.createDoctorSchedule({
        ...form,doctorId:selectedDoctor,
        dayOfWeek:+form.dayOfWeek,
        slotDurationMinutes:+form.slotDurationMinutes,
        maxPatients:form.maxPatients?+form.maxPatients:null,
        consultationFee:+form.consultationFee,
      });
      notify('✅ Schedule added!');setShowForm(false);
      setForm({dayOfWeek:'1',startTime:'09:00',endTime:'17:00',slotDurationMinutes:'15',maxPatients:'',consultationFee:'500'});
      load(selectedDoctor);
    }catch(e:any){notify('❌ '+(e?.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const removeSchedule=async(id:string)=>{
    await api.deleteDoctorSchedule(id);notify('✅ Schedule removed');load(selectedDoctor);
  };

  const saveLeave=async(e:React.FormEvent)=>{
    e.preventDefault();
    if(!selectedDoctor||!leaveForm.leaveDate){notify('⚠️ Select doctor and date');return;}
    setSaving(true);
    try{
      await api.createDoctorLeave({
        ...leaveForm,doctorId:selectedDoctor,
        startTime:leaveForm.isFullDay?null:leaveForm.startTime,
        endTime:leaveForm.isFullDay?null:leaveForm.endTime,
      });
      notify('✅ Leave added!');setShowLeaveForm(false);
      setLeaveForm({leaveDate:'',isFullDay:true,startTime:'',endTime:'',reason:''});
      load(selectedDoctor);
    }catch(e:any){notify('❌ '+(e?.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const removeLeave=async(id:string)=>{
    await api.deleteDoctorLeave(id);notify('✅ Leave cancelled');load(selectedDoctor);
  };

  const byDay:Record<number,any[]>={};
  for(const s of schedules){(byDay[s.dayOfWeek]=byDay[s.dayOfWeek]||[]).push(s);}

  const selectedDoc=doctors.find((d:any)=>d.id===selectedDoctor);
  const workingDays=DAYS.filter((_,i)=>(byDay[i]||[]).length>0).length;

  return(
    <Layout title="Doctor Schedule Management" actions={
      selectedDoctor?(
        <div className="flex gap-2">
          <button onClick={()=>setShowLeaveForm(true)}
            className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm">
            🏖️ Add Leave
          </button>
          <button onClick={()=>setShowForm(true)}
            className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20">
            ➕ Add Schedule
          </button>
        </div>
      ):null
    }>

      {/* Toast */}
      {msg&&(
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border
          ${msg.startsWith('✅')?'bg-green-50 text-green-800 border-green-200':
            msg.startsWith('⚠️')?'bg-amber-50 text-amber-800 border-amber-200':
            'bg-red-50 text-red-800 border-red-200'}`}>
          {msg}
        </div>
      )}

      {/* Doctor selector */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5">
        <label className={LBL}>Select Doctor</label>
        <select
          value={selectedDoctor}
          onChange={e=>setSelectedDoctor(e.target.value)}
          className={INP}
        >
          <option value="">— Select a doctor —</option>
          {doctors.map((d:any)=>{
            const fn=d.firstName||d.first_name||'';
            const ln=d.lastName||d.last_name||'';
            const name=`${fn} ${ln}`.trim();
            return(
              <option key={d.id} value={d.id}>
                {name.startsWith('Dr.')?name:`Dr. ${name}`}
                {d.specialization?` — ${d.specialization}`:''}
              </option>
            );
          })}
        </select>

        {/* Selected doctor info */}
        {selectedDoc&&(
          <div className="mt-4 flex items-center gap-4 p-4 bg-blue-50 rounded-2xl border border-blue-100">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#02357A] to-[#039089] flex items-center justify-center text-white font-black text-lg flex-shrink-0">
              {(selectedDoc.firstName||selectedDoc.first_name||'D')[0].toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-800">
                {(()=>{const n=`${selectedDoc.firstName||selectedDoc.first_name||''} ${selectedDoc.lastName||selectedDoc.last_name||''}`.trim();return n.startsWith('Dr.')?n:`Dr. ${n}`;})()}
              </p>
              {selectedDoc.specialization&&<p className="text-sm text-slate-500">{selectedDoc.specialization}</p>}
            </div>
            <div className="flex gap-4 text-center">
              <div className="bg-white rounded-xl px-4 py-2 border border-blue-100">
                <p className="text-xl font-black text-[#02357A]">{workingDays}</p>
                <p className="text-xs text-slate-400">Working Days</p>
              </div>
              <div className="bg-white rounded-xl px-4 py-2 border border-blue-100">
                <p className="text-xl font-black text-emerald-600">{leaves.length}</p>
                <p className="text-xs text-slate-400">Leaves</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {!selectedDoctor?(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-20 text-center">
          <span className="text-6xl block mb-4">📅</span>
          <h3 className="text-lg font-bold text-slate-700 mb-2">Select a Doctor</h3>
          <p className="text-slate-400 text-sm">Choose a doctor above to view and manage their weekly schedule</p>
        </div>
      ):(
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Weekly schedule — 2/3 width */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
              <h3 className="font-bold text-slate-800">📅 Weekly Schedule</h3>
              <span className="text-xs text-slate-400">{workingDays} active day{workingDays!==1?'s':''}</span>
            </div>

            {loading?(
              <div className="p-8 space-y-3">
                {[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}
              </div>
            ):(
              <div className="divide-y divide-slate-100">
                {DAYS.map((day,i)=>{
                  const slots=byDay[i]||[];
                  const isWorking=slots.length>0;
                  return(
                    <div key={day} className={`flex items-start gap-4 px-5 py-4 transition-colors ${isWorking?'hover:bg-slate-50/50':''}`}>
                      {/* Day indicator */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black border ${isWorking?DAY_COLORS[i]:'bg-slate-50 border-slate-100 text-slate-300'}`}>
                        {day.slice(0,2).toUpperCase()}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-sm ${isWorking?'text-slate-800':'text-slate-400'}`}>{day}</p>
                        {!isWorking?(
                          <p className="text-xs text-slate-300 mt-0.5">Off / Not scheduled</p>
                        ):(
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            {slots.map((s:any)=>{
                              const start=(s.start_time||s.startTime||'').slice(0,5);
                              const end=(s.end_time||s.endTime||'').slice(0,5);
                              return(
                                <div key={s.id} className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-1.5">
                                  <span className="font-mono font-bold text-xs text-[#02357A]">{start}–{end}</span>
                                  <span className="text-xs text-slate-400">{s.slotDurationMinutes||s.slot_duration_minutes}min</span>
                                  <span className="text-xs font-semibold text-emerald-600">{getCurrencySymbol()}{s.consultationFee||s.consultation_fee}</span>
                                  {(s.maxPatients||s.max_patients)&&<span className="text-xs text-slate-400">· max {s.maxPatients||s.max_patients}</span>}
                                  <button onClick={()=>removeSchedule(s.id)}
                                    className="text-slate-300 hover:text-red-500 transition-colors text-sm leading-none ml-1">✕</button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Add slot for this day */}
                      <button
                        onClick={()=>{setForm(f=>({...f,dayOfWeek:String(i)}));setShowForm(true);}}
                        className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 hover:bg-blue-100 hover:text-[#02357A] text-slate-400 flex items-center justify-center text-lg transition-all"
                        title={`Add slot for ${day}`}>
                        +
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Leaves — 1/3 width */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-fit">
            <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
              <h3 className="font-bold text-slate-800">🏖️ Leaves & Blocks</h3>
              <button onClick={()=>setShowLeaveForm(true)}
                className="text-xs font-semibold text-[#02357A] hover:underline">+ Add</button>
            </div>

            {leaves.length===0?(
              <div className="p-10 text-center">
                <p className="text-3xl mb-2">✅</p>
                <p className="text-sm font-medium text-emerald-600">No leave scheduled</p>
                <p className="text-xs text-slate-400 mt-1">Doctor is available all working days</p>
              </div>
            ):(
              <div className="divide-y divide-slate-100">
                {[...leaves].sort((a,b)=>new Date(a.leaveDate||a.leave_date).getTime()-new Date(b.leaveDate||b.leave_date).getTime())
                  .map((l:any)=>{
                    const date=l.leaveDate||l.leave_date;
                    const isPast=new Date(date)<new Date(new Date().setHours(0,0,0,0));
                    return(
                      <div key={l.id} className={`px-4 py-3 ${isPast?'opacity-50':''}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-bold text-sm ${isPast?'text-slate-400':'text-slate-800'}`}>
                              {fmtDate(date)}
                              {isPast&&<span className="ml-2 text-xs font-normal text-slate-400">(past)</span>}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {l.isFullDay||l.is_full_day
                                ?'🕐 Full day'
                                :`🕐 ${l.startTime?.slice(0,5)||l.start_time?.slice(0,5)}–${l.endTime?.slice(0,5)||l.end_time?.slice(0,5)}`}
                              {(l.reason)&&<span> · {l.reason}</span>}
                            </p>
                          </div>
                          {!isPast&&(
                            <button onClick={()=>removeLeave(l.id)}
                              className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors flex-shrink-0">
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Schedule SlidePanel */}
      <SlidePanel isOpen={showForm} onClose={()=>setShowForm(false)} title="➕ Add Schedule Slot" size="md">
        <form onSubmit={saveSchedule} className="space-y-4">
          <div>
            <label className={LBL}>Day of Week</label>
            <select value={form.dayOfWeek} onChange={e=>setForm({...form,dayOfWeek:e.target.value})} className={INP}>
              {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LBL}>Start Time</label>
              <input type="time" value={form.startTime} onChange={e=>setForm({...form,startTime:e.target.value})} className={INP} required/>
            </div>
            <div>
              <label className={LBL}>End Time</label>
              <input type="time" value={form.endTime} onChange={e=>setForm({...form,endTime:e.target.value})} className={INP} required/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LBL}>Slot Duration (min)</label>
              <select value={form.slotDurationMinutes} onChange={e=>setForm({...form,slotDurationMinutes:e.target.value})} className={INP}>
                {['5','10','15','20','30','45','60'].map(m=><option key={m} value={m}>{m} minutes</option>)}
              </select>
            </div>
            <div>
              <label className={LBL}>Consultation Fee ({getCurrencySymbol()})</label>
              <input type="number" value={form.consultationFee} onChange={e=>setForm({...form,consultationFee:e.target.value})} className={INP} placeholder="500"/>
            </div>
          </div>
          <div>
            <label className={LBL}>Max Patients (optional)</label>
            <input type="number" value={form.maxPatients} onChange={e=>setForm({...form,maxPatients:e.target.value})} className={INP} placeholder="Leave blank for slot-based limit"/>
          </div>

          {/* Preview */}
          {form.startTime&&form.endTime&&(
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
              <p className="text-xs font-bold text-[#02357A] uppercase tracking-wider mb-2">Preview</p>
              <p className="text-sm font-semibold text-slate-800">{DAYS[+form.dayOfWeek]} · {form.startTime} – {form.endTime}</p>
              <p className="text-xs text-slate-500 mt-1">
                {Math.floor((new Date(`1970-01-01T${form.endTime}`).getTime()-new Date(`1970-01-01T${form.startTime}`).getTime())/1000/60/+form.slotDurationMinutes)} slots ·
                {form.slotDurationMinutes}min each · {getCurrencySymbol()}{form.consultationFee}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md disabled:opacity-50 flex-1">
              {saving?'Saving…':'✅ Add Schedule'}
            </button>
            <button type="button" onClick={()=>setShowForm(false)}
              className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 text-sm">
              Cancel
            </button>
          </div>
        </form>
      </SlidePanel>

      {/* Add Leave SlidePanel */}
      <SlidePanel isOpen={showLeaveForm} onClose={()=>setShowLeaveForm(false)} title="🏖️ Add Leave / Block Time" size="md">
        <form onSubmit={saveLeave} className="space-y-4">
          <div>
            <label className={LBL}>Date *</label>
            <input type="date" value={leaveForm.leaveDate} onChange={e=>setLeaveForm({...leaveForm,leaveDate:e.target.value})}
              className={INP} min={new Date().toISOString().split('T')[0]} required/>
          </div>

          <label className="flex items-center gap-3 cursor-pointer bg-slate-50 rounded-xl p-3 border border-slate-200">
            <input type="checkbox" checked={leaveForm.isFullDay} onChange={e=>setLeaveForm({...leaveForm,isFullDay:e.target.checked})}
              className="w-4 h-4 accent-[#02357A]"/>
            <div>
              <p className="text-sm font-semibold text-slate-700">Full Day Leave</p>
              <p className="text-xs text-slate-400">Doctor unavailable all day</p>
            </div>
          </label>

          {!leaveForm.isFullDay&&(
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LBL}>From</label>
                <input type="time" value={leaveForm.startTime} onChange={e=>setLeaveForm({...leaveForm,startTime:e.target.value})} className={INP}/>
              </div>
              <div>
                <label className={LBL}>To</label>
                <input type="time" value={leaveForm.endTime} onChange={e=>setLeaveForm({...leaveForm,endTime:e.target.value})} className={INP}/>
              </div>
            </div>
          )}

          <div>
            <label className={LBL}>{t('common.reason','Reason')}</label>
            <input value={leaveForm.reason} onChange={e=>setLeaveForm({...leaveForm,reason:e.target.value})}
              className={INP} placeholder="e.g. Conference, Personal leave, Holiday"/>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md disabled:opacity-50 flex-1">
              {saving?'Saving…':'🏖️ Add Leave'}
            </button>
            <button type="button" onClick={()=>setShowLeaveForm(false)}
              className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 text-sm">
              Cancel
            </button>
          </div>
        </form>
      </SlidePanel>
    </Layout>
  );
}
